import React, { Fragment, useCallback, useEffect, useState } from 'react';
import Button from 'view/common/elements/button';
import TextBox from 'view/common/elements/textbox';
import httpClient from 'httpClient';
import Select from 'view/common/elements/select';
import Notification from 'view/common/notification';
import AllResources from './allResources';

import { HAZARDS, LONGITS, DISTRIBUTION } from './common';
import AllExistingHazards from './allExistingHazards';
import AllCharts from './allCharts';
import OutlineButton from 'view/common/elements/outlineButton';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';

const SaveHazards = (props) => {
  const history = useHistory();
  const location = useLocation();

  const EDIT_VIEW= 'EDIT';
  const LIST_VIEW= 'LIST';
  const curveTypeOptions = [
    {label:'Hazards', value:HAZARDS},
    {label:'Longitudinal', value:LONGITS},
    {label:'Distribution', value:DISTRIBUTION},
  ]

  const hazardsSaveUrl = "/administration/v1/saveHazards";
  const hazardsGetUrl = "/administration/v1/getHazards";
  const longitsSaveUrl = "/administration/v1/saveLongitudinalHazards";
  const longitsGetUrl = "/administration/v1/getLongitudinalHazards";
  const distributionSaveUrl = "/administration/v1/saveHazardsDistribution";
  const distributionGetUrl = "/administration/v1/getHazardDistribution";

  const [curveType, setCurveType] = useState(HAZARDS);
  const [biomarkersLoading, setBiomarkersLoading] = useState(true);
  const [unitsLoading, setUnitsLoading] = useState(true);
  const [allBiomarkers, setAllBioMarkers] = useState([]);
  const [hazards, setHazards] = useState('');
  const [selectedBiomarker, setSelectedBiomarker] = useState(null);
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [biomarkerMap, setBiomarkerMap] = useState({});

  const [biomarkerError, setBiomarkerError] = useState('');
  const [unitError, setUnitError] = useState('');
  const [hazardsError, setHazardsError] = useState('');

  const [dataSaving, setDataSaving] = useState(false);

  const [loadingExistingHazards, setLoadingExistingHazards] = useState(false);
  const [existingHazards, setExistingHazards] = useState([]);

  const [allDataSelected, setAllDataSelected] = useState(false);
  
  const [view, setView] = useState(EDIT_VIEW);

  const formatAndSetHazards = (hazards) => {
    //format
    
    setHazards(hazards.replace(/\t+/g, ","))
  }

  const saveHazards = async () => {
    if(!selectedBiomarker || !selectedBiomarker.value) {
      setBiomarkerError("Please select a biomarker");
      return false;
    }
    if(!hazards) {
      setHazardsError("Please enter hazard rates");
      return false;
    }
    if(!selectedUnit || !selectedUnit.value) {
      setUnitError("Please enter a unit");
      return false;
    }
    const payload = {
      biomarker: selectedBiomarker.value,
      hazards: hazards,
      unit: selectedUnit.value,
    }

    let url= ''
    switch(curveType) {
      case HAZARDS:
        url=hazardsSaveUrl;
        break;
      case LONGITS:
        url=longitsSaveUrl;
        break;
      case DISTRIBUTION:
        url=distributionSaveUrl;
        break;
      default:
        throw new Error("Unknown curve type");
    }
    
    setDataSaving(true)

    let response;
    try {
      response = await httpClient.post(url, payload);
      if(!response.error) {
        Notification.success(`${response.linesUpdated} hazard rates were saved`)
      }
      loadExistingHazards()
    } catch (ex) {
      Notification.error("An error was encountered")
    }
    
    setDataSaving(false)
  }
  const loadBiomarkers = async () => {
    setBiomarkersLoading(true);
    const response = await httpClient.get("/administration/v1/getBiomarkers");
    if(response.error) {
      return;
    } else {
      const bmMap = {};
      const biomarkers = [];
      response.map(biomarker => {
        bmMap[biomarker.id] = biomarker;
        return false;
      })
      Object.keys(bmMap).map(id => {
        biomarkers.push({
          'name':bmMap[id].name + (bmMap[id].shortName ? (' ['+bmMap[id].shortName+']') : ''),
          id:bmMap[id].id,
        });
        return false;
      })
      setAllBioMarkers(biomarkers)
      setBiomarkersLoading(false);
      setBiomarkerMap(bmMap);
    }
  }

  const loadUnits = async () => {
    setUnitsLoading(true);
    const response = await httpClient.get("/administration/v1/getUnits");
    if(response.error) {
      return;
    } else {
      setUnits(response)
      setUnitsLoading(false);
    }
    
  }

  const onBiomarkerSelect = (v) => {
    if(!v) {
      return;
    }
    setSelectedBiomarker(v)
    setSelectedUnit({value:biomarkerMap[v.value].unitId, label:biomarkerMap[v.value].unit, })
  }

  const curveTypeChanged = (v) => {
    
    setCurveType(v.value)
    const searchParams = new URLSearchParams();
    searchParams.set('type', v.value);
    
    // Update URL with query parameters
    history.push({
      pathname: '/administration/saveHazards',
      search: searchParams.toString()
    });
  }

  const loadExistingHazards = useCallback(async () => {
    if(!selectedBiomarker || !selectedBiomarker.value) {
      return false;
    }
    
    if(!selectedUnit || !selectedUnit.value) {
      return false;
    }
    const payload = {
      biomarker: selectedBiomarker.value,
      unit: selectedUnit.value,
    }
    let url='';
    switch(curveType) {
      case HAZARDS:
        url=hazardsGetUrl;
        break;
      case LONGITS:
        url=longitsGetUrl;
        break;
      case DISTRIBUTION:
        url=distributionGetUrl;
        break;
      default:
        throw new Error("Unknown curve type");
    }
    setLoadingExistingHazards(true)
    let response;

    try {
      response = await httpClient.post(url, payload);
      if(!response.error) {
        setHazards(getInputFormat(response.hazardRates));
        setExistingHazards(response.hazardRates)
      }
      setView(LIST_VIEW);
    } catch (ex) {
      Notification.error("An error was encountered")
    }
    setLoadingExistingHazards(false)
  
  },[selectedUnit, curveType]);

  const getInputFormat = (existingHazards) => {
    let input = [];
    if(curveType == HAZARDS) {
      for(let i=0;i<existingHazards.length;i++) {
        input.push([existingHazards[i].biomarkerValue,existingHazards[i].hazardRate]);
      }
    } else if(curveType == LONGITS) {
      for(let i=0;i<existingHazards.length;i++) {
        input.push([existingHazards[i].age,existingHazards[i].modelBiomarkerValue,existingHazards[i].hazardRate]);
      }
    }
    return input.join("\n");
  }

  const getInputPlaceHolder = () => {
    switch(curveType) {
      case HAZARDS:
        return "Enter Hazard rates as value, hazard rate eg {2.3, 1.01}";
      case LONGITS:
        return "Enter Hazard rates as age, value, hazard rate eg {35, 2.3, 1.01}";
      default:
        return "This feature is under construction"
    }
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const typeFromUrl = searchParams.get('type');
    
    if (typeFromUrl) {
      // const selected = names.find(name => name.id.toString() === idFromUrl);
      setCurveType(typeFromUrl);
    }
  }, [location.search]);

  useEffect(()=>{
    loadBiomarkers()
    loadUnits()
  },[])

  useEffect(()=>{
    setDataLoaded(!unitsLoading && !biomarkersLoading);
  },[unitsLoading, biomarkersLoading])

  useEffect(()=>{
    if(selectedBiomarker && selectedBiomarker.value && selectedUnit && selectedUnit.value) {
      setAllDataSelected(true)
    } else {
      setAllDataSelected(false)
    }
    loadExistingHazards()
  },[selectedUnit,  loadExistingHazards, curveType])

  return ( 
    <div >
      <div>
        {/* <div className='w-1/3 inline-block'>
          <div className='text-3xl text-center mb-5'>Save Longitudinal Hazard Rates</div>
        </div>
        <div className='w-1/3 inline-block'></div> */}
      </div>
      <div className='flex items-stretch'>
        <div className='w-1/2 mr-2 inline-block'> 
        <div className='mb-2 flex'>
          {!dataLoaded && <div> Loading ... </div>}
          {
            dataLoaded && 
            <Fragment>
              <div className='w-1/3 inline-block mr-1'>
                <Select 
                  options={allBiomarkers} 
                  onChange={v => {onBiomarkerSelect(v)} }
                  labelKey="name"
                  valueKey="id"
                  value={selectedBiomarker}
                  placeholder="Select Biomarker"
                  error={biomarkerError}
                  onFocus={() => setBiomarkerError('') }
                />
              </div>
              <div className='w-1/3 inline-block ml-2'>
                <Select 
                  options={units} 
                  onChange={v => {setSelectedUnit(v)} }
                  labelKey="name"
                  valueKey="id"
                  
                  placeholder="Select Unit"
                  value={selectedUnit}
                  error={unitError}
                  onFocus={() => setUnitError('') }
                />
              </div>
              <div className='w-1/3 inline-block ml-2'>
                <Select 
                  options={curveTypeOptions} 
                  onChange={curveTypeChanged}
                  labelKey="label"
                  valueKey="value"
                  
                  placeholder="Select Unit"
                  value={curveType}
                  error={unitError}
                  onFocus={() => setUnitError('') }
                  isClearable={false}
                />
              </div>  
            </Fragment>
          }
        </div>
          {view === EDIT_VIEW &&
            <div className='mb-2 w-full inline-block'>
              
              <div>
              
                <TextBox
                  content={hazards}
                  name={"hazards"}
                  onChange={formatAndSetHazards}
                  error={hazardsError}
                  onFocus={() => setHazardsError('') }
                  placeholder={getInputPlaceHolder()}
                  
                  height="23"
                />

              </div>
              <Button 
                text={"Save"}
                onClick={saveHazards}
                loading={dataSaving}
                className={'blue'}
              />
              <OutlineButton 
                text={"Cancel"}
                onClick={() => setView(LIST_VIEW)}
                className={`ml-2 mb-10 mt-2` }
              />
            </div>
          }
          {
            view === LIST_VIEW &&
            <div className='mb-2 w-full inline-block relative'>
              {
                loadingExistingHazards && <div>Loading ...</div>
              }
              {
                !loadingExistingHazards && allDataSelected && 
                <AllExistingHazards 
                  existingHazards={existingHazards}
                  unit={selectedUnit?.label}
                  biomarker={selectedBiomarker?.label}
                  type={curveType}
                  onEditClick={()=>setView(EDIT_VIEW)}
                />
              }
            </div>
          }
          
        </div>
        <div className='mb-2 mr-2 w-1/2 inline-block'>
          {
            !loadingExistingHazards && allDataSelected && (existingHazards.length > 0) &&
            <Fragment>
              <AllResources
                unit={selectedUnit?.value}
                biomarker={selectedBiomarker?.value}
                type={curveType}
              />
              
              <AllCharts
                existingHazards={existingHazards}
                type={curveType}
              />
              
            </Fragment>
          }

          
        </div>
      </div>
    </div>
  )  
}

export default SaveHazards;